import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Clock, CheckCircle2, FileText, CheckSquare } from 'lucide-react';
import { getQuestionsByExamId } from '../utils/questionAPI';
import { mapTypeIdToName, getTypeIdByCode } from '../utils/questionTypesMapAPI';
import { 
  getExamById, 
  getExamSubjects, 
  calculateTotalQuestions 
} from '../utils/examAPI';
import { 
  getSubjectQuestionConfig, 
  parseTypeCount, 
  calculateSubjectTotalQuestions 
} from '../utils/subjectsAPI';
import ExamInfoTable from '../components/ExamInfoTable';

// 获取题型名称函数
const getTypeName = (type) => {
  switch(type) {
    case '1': return '单选题';
    case '3': return '多选题';
    case '2': return '判断题';
    case '单选': return '单选题';
    case '多选': return '多选题';
    case '判断': return '判断题';
    default: return `题型${type}`;
  }
};

function ExamStart({ session }) {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectQuestions, setSubjectQuestions] = useState({});
  const [totalQuestionCount, setTotalQuestionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSubject, setPendingSubject] = useState(null);

  useEffect(() => {
    const fetchExamDetails = async () => {
      try {
        setLoading(true);
        
        const user = localStorage.getItem('user_id') || session?.user?.id;
        if (!user) {
          navigate('/login');
          return;
        }
        
        // 获取考试信息
        const examData = await getExamById(examId);
        setExam(examData);
        
        // 获取科目信息
        const subjectsData = await getExamSubjects(examId);
        setSubjects(subjectsData || []);
        
        // 获取科目的详细题型设置 (subject_question表)
        const subjectQsData = {};
        
        for (const subject of subjectsData || []) {
          const sqData = await getSubjectQuestionConfig(subject._id);
          if (sqData && sqData.length > 0) {
            subjectQsData[subject._id] = sqData;
          }
        }
        
        setSubjectQuestions(subjectQsData);
        
        // 计算考试题目总数
        const count = subjectsData.reduce((sum, s) => {
          // 内联定义计算函数，解决依赖问题
          const calculateSubjectTotalQuestionsInner = (subject) => {
            // 首先从subject_question表中的配置计算
            const sqConfigs = subjectQsData[subject._id];
            if (sqConfigs && sqConfigs.length > 0) {
              return sqConfigs.reduce((sum, sq) => sum + sq.question_count, 0);
            }
            
            // 如果没有subject_question配置，尝试从type_count字段计算
            const typeCount = parseTypeCount(subject.type_count);
            if (typeCount) {
              return Object.values(typeCount).reduce((sum, count) => sum + count, 0);
            }
            
            // 如果都没有，使用count字段
            return subject.count || 0;
          };

          return sum + calculateSubjectTotalQuestionsInner(s);
        }, 0);
        
        setTotalQuestionCount(count);
      } catch (error) {
        console.error('Error fetching exam details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExamDetails();
  }, [examId, session, navigate]);

  const handleStartSubjectExam = (subject) => {
    setPendingSubject(subject);
    setShowConfirm(true);
  };

  const handleConfirmStart = () => {
    setShowConfirm(false);
    if (pendingSubject) {
      navigate(`/subject-exam/${examId}/${pendingSubject._id}`);
      setPendingSubject(null);
    }
  };

  const handleCancelStart = () => {
    setShowConfirm(false);
    setPendingSubject(null);
  };

  // 计算科目题目总数（优先从subject_question关系表获取）
  const calculateSubjectTotalQuestions = (subject) => {
    // 首先从subject_question表中的配置计算
    const sqConfigs = subjectQuestions[subject._id];
    if (sqConfigs && sqConfigs.length > 0) {
      return sqConfigs.reduce((sum, sq) => sum + sq.question_count, 0);
    }
    
    // 如果没有subject_question配置，尝试从type_count字段计算
    const typeCount = parseTypeCount(subject.type_count);
    if (typeCount) {
      return Object.values(typeCount).reduce((sum, count) => sum + count, 0);
    }
    
    // 如果都没有，使用count字段
    return subject.count || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen app-bg-color">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="text-gray-500 font-medium">加载考试信息中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col app-bg-color">
      <Header title={
        <div className="flex flex-col">
          <span className="text-base font-bold">考试详情</span>
        </div>
      } />
      
      <div className="flex-1 p-4 pb-6">
        {exam ? (
          <>
            <div className="mb-6 bg-white p-5 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <div className="w-1.5 h-6 bg-primary rounded-full mr-2"></div>
                {exam.name}
              </h2>
              
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="px-4 py-1.5 bg-blue-100 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                  <FileText className="w-4 h-4 mr-1.5" />
                  总题目: {totalQuestionCount > 0 ? totalQuestionCount : (exam.count || '未设置')}题
                </div>
              </div>
              
              <div className="text-gray-600 text-sm mb-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="font-medium mb-2 text-gray-700 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-1.5 text-primary" />
                  考试说明：
                </p>
                <p className="leading-relaxed">{exam.description || `此考试共包含${subjects.length}个考试科目，需要分开进行考试。请在规定时间内完成所有题目并提交，提交后系统将自动判分。`}</p>
              </div>
            </div>

            {subjects.map((subject, index) => {
              const typeCount = parseTypeCount(subject.type_count);
              const subjectQs = subjectQuestions[subject._id] || [];
              const totalQs = calculateSubjectTotalQuestions(subject);
              
              // 准备考试信息数据
              const examInfo = {
                examType: subject.description || '未指定考试类型',
                duration: subject.duration || '未指定',
                passingScore: subject.pass_score || '未指定',
                examRule: subject.rules || `共${totalQs}题，包含${
                  Object.entries(typeCount).map(([type, count]) => 
                    `${getTypeName(type)}${count}题`
                  ).join('、')
                }`
              };
              
              return (
                <div 
                  key={subject._id} 
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5 flex flex-col relative overflow-hidden"
                >
                  {/* 背景装饰 */}
                  <div className="absolute -right-10 -top-10 w-40 h-40 bg-primary/5 rounded-full"></div>
                  
                  {/* 科目名与描述 */}
                  <div className="flex items-center mb-4 relative">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                      <CheckSquare className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">{subject.name}</div>
                    </div>
                  </div>
                  
                  {/* 使用ExamInfoTable组件显示科目信息 */}
                  <ExamInfoTable examInfo={examInfo} />
                  
                  {/* 操作按钮 */}
                  <button
                    onClick={() => handleStartSubjectExam(subject)}
                    className="mt-5 w-full bg-gradient-to-r from-primary-dark to-primary hover:bg-primary-dark text-white py-3 rounded-lg text-base font-semibold transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    开始考试
                  </button>
                </div>
              );
            })}
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mt-2">
              <h4 className="font-semibold mb-3 flex items-center text-gray-800">
                <Clock className="w-4 h-4 text-primary mr-2" />
                注意事项：
              </h4>
              <ul className="list-disc pl-5 space-y-2 text-gray-600 text-sm">
                <li>每个科目单独考试，请逐一完成</li>
                <li>答题时间有限，请合理安排时间</li>
                <li>提交后无法修改答案</li>
                <li>为了公平起见，请独立完成考试</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-white rounded-xl shadow-sm p-6 mt-4">
            <FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">未找到考试信息</p>
            <p className="text-sm mt-2 text-gray-400">请返回选择其他考试</p>
          </div>
        )}
      </div>

      {/* 弹窗 */}
      {showConfirm && pendingSubject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[90%] max-w-sm border border-gray-100 transform transition-all duration-300 scale-100">
            <div className="text-xl font-bold mb-4 text-gray-800 flex items-center">
              <CheckCircle2 className="w-5 h-5 text-primary mr-2" />
              考试须知
            </div>
            
            <div className="text-gray-700 mb-6 bg-blue-50 p-4 rounded-lg text-sm leading-relaxed border border-blue-100">
              本场考试为<span className="text-primary font-bold mx-1">《{pendingSubject.name}》</span>
              <div className="mt-2 flex items-center">
                <Clock className="w-4 h-4 text-blue-500 mr-1.5" />
                <span>考试时长：<span className="text-primary font-semibold">{pendingSubject.duration || '--'}</span>分钟</span>
              </div>
              <div className="mt-1">点击"开始考试"后计时将立即开始，请确保做好准备。</div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelStart}
                className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmStart}
                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-primary-dark to-primary text-white hover:shadow-md font-medium transition-all flex items-center"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                开始考试
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamStart; 